import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Switch, 
  Select, 
  Space, 
  Typography, 
  Divider,
  Row,
  Col,
  Avatar,
  Upload,
  message,
  Tabs,
  Alert
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  BellOutlined, 
  SettingOutlined,
  UploadOutlined,
  SaveOutlined,
  SecurityScanOutlined,
  FileTextOutlined,
  TeamOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import apiService, { getTeamPointConfig, updateTeamPointConfig, getProfileUserInfo } from '../services/api';
import { getRoleLabel, getBusinessAreaLabel, isTeamLeader } from '../utils/roleUtils';
import DEPLOYMENT_CONFIG from '../config/deployment';
import Loader from './Loader';



const { Title, Text } = Typography;
const { Option } = Select;

const Settings: React.FC = () => {
  const { user, refreshUser } = useCognitoAuth();
  const [profileForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [teamPointConfig, setTeamPointConfig] = useState<any>(null);
  const [teamPointConfigLoading, setTeamPointConfigLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (user) {
      // Set initial form values from user data
      profileForm.setFieldsValue({
        username: user.username,
        email: user.email,
        role: user.role,
        business_area: user.business_area,
        language: 'pt',
      });
      
      // Load user info to check if they are a team leader
      loadUserInfo();
    }
  }, [user, profileForm]);

  const loadUserInfo = async () => {
    try {
      const userData = await getProfileUserInfo();
      setUserInfo(userData);
      
      // Load team point configuration if user is a team leader
      if (userData.is_team_leader && userData.team_info?.id) {
        loadTeamPointConfig(userData.team_info.id);
      }
    } catch (error) {
    } finally {
      setInitialLoading(false);
    }
  };

  const loadTeamPointConfig = async (teamId: number) => {
    setTeamPointConfigLoading(true);
    try {
      const response = await getTeamPointConfig(teamId);
      if (response.success) {
        setTeamPointConfig(response.data);
      }
    } catch (error) {
      message.error('Erro ao carregar configuração de pontos da equipa');
    }
    setTeamPointConfigLoading(false);
  };

  const handleTeamPointConfigSave = async (values: any) => {
    if (!userInfo?.team_info?.id) {
      message.error('ID da equipa não disponível');
      return;
    }
    
    setTeamPointConfigLoading(true);
    try {
      const response = await updateTeamPointConfig(userInfo.team_info.id, values);
      
      if (response.success) {
        message.success('Configuração de pontos atualizada com sucesso!');
        setTeamPointConfig(response.data);
      } else {
        message.error(response.message || 'Erro ao atualizar configuração de pontos');
      }
    } catch (error) {
      message.error('Erro ao atualizar configuração de pontos');
    }
    setTeamPointConfigLoading(false);
  };

  const handleProfileSave = async (values: any) => {
    if (!user) return;
    setLoading(true);
    try {
      // First upload avatar if there's a selected file
      if (avatarFile) {
        try {
          const avatarResponse = await apiService.uploadAvatar(avatarFile);
          
          if (avatarResponse.success) {
            message.success('Foto de perfil carregada com sucesso!');
            // Clear the avatar file and preview
            setAvatarFile(null);
            setAvatarPreview(null);
          } else {
            message.error(avatarResponse.message || 'Erro ao carregar foto de perfil.');
            setLoading(false);
            return;
          }
        } catch (error: any) {
          if (error.response?.status === 401) {
            message.error('Sessão expirada. Por favor, faça login novamente.');
            // Redirect to login
            window.location.href = '/login';
            return;
          } else {
            message.error('Erro ao carregar foto de perfil: ' + (error.response?.data?.message || error.message));
          }
          setLoading(false);
          return;
        }
      }

      // Then update other profile data - only send allowed fields
      const allowedFields = ['email']; // Only email is allowed to be updated by users
      const filteredValues = Object.keys(values)
        .filter(key => allowedFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = values[key];
          return obj;
        }, {} as any);
      
      const updateResponse = await apiService.updateCurrentUser(filteredValues);
      
      if (updateResponse.success) {
        message.success('Perfil atualizado com sucesso!');
        
        // Update context with the returned user data
        if (updateResponse.user) {
          localStorage.setItem('user', JSON.stringify(updateResponse.user));
          refreshUser();
        }
        
        // Force a page reload to ensure avatar is displayed correctly
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        message.error(updateResponse.message || 'Erro ao atualizar perfil.');
        setLoading(false);
        return;
      }
      
    } catch (error) {
      message.error('Erro ao atualizar perfil.');
    }
    setLoading(false);
  };

  const handleSecuritySave = async (values: any) => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await apiService.changePassword(values.current_password, values.new_password);
      
      if (response.success) {
        message.success('✅ Palavra-passe alterada com sucesso! A sua nova palavra-passe está agora ativa.');
        securityForm.resetFields();
      } else {
        message.error(response.message || 'Erro ao alterar palavra-passe.');
      }
    } catch (error) {
      message.error('Erro ao alterar palavra-passe.');
    }
    setLoading(false);
  };

  const handleNotificationSave = async (values: any) => {
    if (!user) return;
    setLoading(true);
    try {
      // This would be implemented in the backend
  
      message.success('Preferências de notificação atualizadas!');
    } catch (error) {
      message.error('Erro ao atualizar preferências de notificação.');
    }
    setLoading(false);
  };



  const tabItems = [
    {
      key: 'profile',
      label: (
        <span>
          <UserOutlined />
          Perfil
        </span>
      ),
      children: (
        <Card>
          <Row gutter={24}>
            <Col span={8}>
                              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Avatar 
                  size={120} 
                  src={avatarPreview || (user?.avatar_url ? (user.avatar_url.startsWith('data:image/') ? user.avatar_url : `${DEPLOYMENT_CONFIG.getBackendUrl()}/${user.avatar_url}`) : undefined)}
                  icon={<UserOutlined />} 
                  style={{ marginBottom: 16 }}
                />
                <div style={{ marginBottom: 16 }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Validate file type
                        const isImage = file.type.startsWith('image/');
                        if (!isImage) {
                          message.error('Só pode carregar ficheiros de imagem!');
                          return;
                        }
                        
                        // Validate file size (max 2MB)
                        const isLt2M = file.size / 1024 / 1024 < 2;
                        if (!isLt2M) {
                          message.error('A imagem deve ser menor que 2MB!');
                          return;
                        }

                        setAvatarFile(file);
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          setAvatarPreview(e.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                        message.success('Imagem selecionada com sucesso!');
                      }
                    }}
                    style={{ display: 'none' }}
                    id="avatar-input"
                  />
                  <Button 
                    icon={<UploadOutlined />}
                    onClick={() => document.getElementById('avatar-input')?.click()}
                  >
                    Selecionar Foto
                  </Button>
                </div>
              </div>
            </Col>
            
            <Col span={16}>
              <Form
                form={profileForm}
                layout="vertical"
                onFinish={handleProfileSave}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="username"
                      label="Nome de Utilizador"
                      rules={[{ required: true, message: 'Por favor insira o nome de utilizador!' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: 'Por favor insira o email!' },
                        { type: 'email', message: 'Por favor insira um email válido!' }
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="role"
                      label="Função"
                    >
                      <Input disabled value={getRoleLabel(user?.role || '')} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="business_area"
                      label="Área de Negócio"
                    >
                      <Input disabled value={getBusinessAreaLabel(user?.business_area || '')} />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="language"
                      label="Idioma"
                    >
                      <Select>
                        <Option value="pt">Português</Option>
                        <Option value="en">English</Option>
                        <Option value="es">Español</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Form.Item>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                    Guardar Alterações
                  </Button>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </Card>
      )
    },
    {
      key: 'security',
      label: (
        <span>
          <LockOutlined />
          Segurança
        </span>
      ),
      children: (
        <Card>
          <Form
            form={securityForm}
            layout="vertical"
            onFinish={handleSecuritySave}
          >
            <Alert
              message="Alteração de Palavra-passe"
              description="Para alterar a sua palavra-passe, insira a nova palavra-passe. Se souber a palavra-passe atual, pode inseri-la para maior segurança."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="current_password"
                  label="Palavra-passe Atual (Opcional)"
                  rules={[]}
                >
                  <Input.Password placeholder="Deixe em branco se não souber a palavra-passe atual" />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="new_password"
                  label="Nova Palavra-passe"
                  rules={[
                    { required: true, message: 'Por favor insira a nova palavra-passe!' },
                    { min: 8, message: 'A palavra-passe deve ter pelo menos 8 caracteres!' },
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        const hasUpperCase = /[A-Z]/.test(value);
                        const hasLowerCase = /[a-z]/.test(value);
                        const hasNumber = /[0-9]/.test(value);
                        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
                        
                        if (!hasUpperCase) {
                          return Promise.reject(new Error('A palavra-passe deve conter pelo menos uma letra maiúscula!'));
                        }
                        if (!hasLowerCase) {
                          return Promise.reject(new Error('A palavra-passe deve conter pelo menos uma letra minúscula!'));
                        }
                        if (!hasNumber) {
                          return Promise.reject(new Error('A palavra-passe deve conter pelo menos um número!'));
                        }
                        if (!hasSpecialChar) {
                          return Promise.reject(new Error('A palavra-passe deve conter pelo menos um carácter especial (!@#$%^&*...)!'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  help="Requisitos: Mínimo 8 caracteres, 1 maiúscula, 1 minúscula, 1 número, 1 carácter especial"
                >
                  <Input.Password />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="confirm_password"
                  label="Confirmar Nova Palavra-passe"
                  rules={[
                    { required: true, message: 'Por favor confirme a nova palavra-passe!' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('new_password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('As palavras-passe não coincidem!'));
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </Col>
            </Row>
            
            <Divider />
            
            {user?.role === 'admin' && (
              <div>
                <Title level={4}>
                  <SecurityScanOutlined /> Autenticação de Dois Fatores (2FA)
                </Title>
                <Alert
                  message="2FA Ativo"
                  description="A autenticação de dois fatores está ativa para a sua conta de administrador."
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Text type="secondary">
                  Como administrador, a autenticação de dois fatores é obrigatória para a segurança do sistema.
                </Text>
              </div>
            )}
            
            <Form.Item style={{ marginTop: 24 }}>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                Alterar Palavra-passe
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )
    },
    {
      key: 'notifications',
      label: (
        <span>
          <BellOutlined />
          Notificações
        </span>
      ),
      children: (
        <Card>
          <Form
            form={notificationForm}
            layout="vertical"
            onFinish={handleNotificationSave}
            initialValues={{
              in_app_notifications: true,
              lead_assignments: true,
              duplicate_leads: true,
              calendar_reminders: true,
              team_updates: true,
            }}
          >
            <Title level={4}>Preferências de Notificação</Title>
            
            <Alert
              message="Notificações na Aplicação"
              description="Todas as notificações são mostradas na aplicação. Configure quais tipos de notificação deseja receber."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <Title level={5}>Tipos de Notificação</Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="lead_assignments"
                  label={
                    <span>
                      <FileTextOutlined /> Atribuições de Leads
                    </span>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="duplicate_leads"
                  label={
                    <span>
                      <FileTextOutlined /> Leads Duplicados
                    </span>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="calendar_reminders"
                  label={
                    <span>
                      <BellOutlined /> Lembretes do Calendário
                    </span>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="team_updates"
                  label={
                    <span>
                      <SettingOutlined /> Atualizações da Equipa
                    </span>
                  }
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item style={{ marginTop: 24 }}>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
                Guardar Preferências
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )
    }
  ];

  // Add team point configuration tab for team leaders
  if (userInfo && userInfo.is_team_leader && userInfo.team_info?.id) {
    tabItems.push({
      key: 'team-points',
      label: (
        <span>
          <TeamOutlined />
          Configuração de Pontos da Equipa
        </span>
      ),
      children: (
        <Card>
          <Title level={4}>
            <DollarOutlined /> Configuração de Pontos da Equipa
          </Title>
          <Alert
            message="Configuração de Pontos"
            description="Configure os valores de pontos para cada tipo de pacote e cartões adicionais. Estas configurações serão aplicadas aos cálculos de rendimento de todos os membros da sua equipa."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          
          <Form
            key={teamPointConfig ? 'loaded' : 'default'}
            layout="vertical"
            onFinish={handleTeamPointConfigSave}
            initialValues={teamPointConfig || {
              package_2p_points: 20,
              package_3p_points: 80,
              package_4p1_points: 100,
              package_4p2_points: 120,
              additional_cards_1_points: 5,
              additional_cards_2_points: 10,
              additional_cards_3_points: 15,
              bracket_prata_bonus: 0,
              bracket_ouro_bonus: 0,
              bracket_diamante_bonus: 0
            }}
          >
            <Title level={5}>Tipologia de Pacote</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="package_2p_points"
                  label="2P (Pontos)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de pontos para 2P!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="package_3p_points"
                  label="3P (Pontos)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de pontos para 3P!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="package_4p1_points"
                  label="4P1 (Pontos)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de pontos para 4P1!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="package_4p2_points"
                  label="4P2 (Pontos)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de pontos para 4P2!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
            </Row>
            
            <Divider />
            
            <Title level={5}>Cartões Adicionais</Title>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="additional_cards_1_points"
                  label="1 Cartão (Pontos)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de pontos para 1 cartão!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="additional_cards_2_points"
                  label="2 Cartões (Pontos)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de pontos para 2 cartões!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="additional_cards_3_points"
                  label="3 Cartões (Pontos)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de pontos para 3 cartões!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
            </Row>
            
            <Divider />
            
            <Title level={5}>Bónus por Escalão</Title>
            <Alert
              message="Bónus por Escalão"
              description="Configure o valor de bónus adicional para cada escalão. O escalão Iniciante não tem bónus."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="bracket_prata_bonus"
                  label="Prata (Bónus)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de bónus para Prata!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="bracket_ouro_bonus"
                  label="Ouro (Bónus)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de bónus para Ouro!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="bracket_diamante_bonus"
                  label="Diamante (Bónus)"
                  rules={[
                    { required: true, message: 'Por favor insira o valor de bónus para Diamante!' }
                  ]}
                >
                  <Input type="number" min={0} />
                </Form.Item>
              </Col>
            </Row>
            
            <Form.Item style={{ marginTop: 24 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />} 
                loading={teamPointConfigLoading}
              >
                Guardar Configuração
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )
    });
  }

  if (!user) {
    return null;
  }

  if (initialLoading) {
    return (
      <div style={{ 
        padding: '16px', 
        minHeight: '100vh',
        width: '90%',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ 
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: '48px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <Loader />
          <Title level={3} style={{ color: '#1f2937', marginBottom: '8px', marginTop: '20px' }}>
            A carregar as suas configurações
          </Title>
          <Text style={{ color: '#6b7280', fontSize: '16px' }}>
            A preparar os seus dados de perfil...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Title level={2}>Configurações</Title>
      
      <Tabs 
        defaultActiveKey="profile" 
        size="large"
        items={tabItems}
      />
    </div>
  );
};

export default Settings; 